// MessageCategoryTabAllInOne.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import FeedComponent from '../container/FeedComponent.js';

import { parseCategoryString } from '../component/FeedComponent.jsx';

import { HomeCategoryUi } from '../resource/feedConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { CategoryListType } from '../resource/categoryConstants.js';

import { color, footerNavigationHeight } from '../style/variables.js';
import media from '../style/media.js';

import EmptyIconSrouce from '../../img/img-empty-category-feed.svg';

export const MessageCategoryTabAllInOne = ({
  categoryId = null,
  isFetchedAndEmpty = false,
  categoryTemplates = [],
  categoryFeeds = [],
}) => {
  const { t } = useTranslation();
  const i18nKeys = useMemo(
    () => categoryTemplates.map(key => getTranslationKey({ key })),
    [categoryTemplates]
  );
  const result = useMemo(() => {
    if (isFetchedAndEmpty) {
      return (
        <EmptyWrapper>
          <EmptyIcon src={EmptyIconSrouce} />
          {t('it_is_empty_here', {
            ns: TranslationNamespace.GENERAL,
          })}
        </EmptyWrapper>
      );
    }
    return categoryFeeds.map((category, index) => {
      const { ui } = parseCategoryString({ category });
      let titleLink;
      switch (ui) {
        case HomeCategoryUi.FLIX: {
          titleLink = `/category/${categoryId}/${CategoryListType.FLIX}`;
          break;
        }
        case HomeCategoryUi.SHORTS:
        case HomeCategoryUi.SHORTS_METADATA_CARD: {
          titleLink = `/category/${categoryId}/${CategoryListType.SHORT}`;
          break;
        }
        case HomeCategoryUi.STORY:
        case HomeCategoryUi.STORY_METADATA_CARD:
        case HomeCategoryUi.STORY_GRID: {
          titleLink = `/category/${categoryId}/${CategoryListType.STORY}`;
          break;
        }
        case HomeCategoryUi.POST_CARD_GRID:
        case HomeCategoryUi.MESSAGE_LIST: {
          titleLink = `/category/${categoryId}/${CategoryListType.IMAGE}`;
          break;
        }
        default:
          break;
      }
      return (
        <MessageCategory key={category} className={`categoryId_${category}`}>
          <FeedComponent
            category={category}
            categoryIndex={index}
            shouldShowMoreLink={false}
            shouldShowTotalCount
            shouldShowUsername
            titleI18nId={i18nKeys[index]}
            titleLink={titleLink}
            isProfileCategory
            isTitleClickable
          />
        </MessageCategory>
      );
    });
  }, [isFetchedAndEmpty, categoryFeeds, t, i18nKeys, categoryId]);
  return result;
};

MessageCategoryTabAllInOne.propTypes = {
  categoryId: PropTypes.string,
  isFetchedAndEmpty: PropTypes.bool,
  categoryTemplates: PropTypes.array,
  categoryFeeds: PropTypes.array,
};

const EmptyWrapper = styled.div`
  padding-top: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 14px;
  color: ${color.neutral[30]};
`;

const EmptyIcon = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const MessageCategory = styled.div`
  :last-child {
    margin-bottom: 20px;
    ${media.mobile`
      margin-bottom: calc(12px + ${footerNavigationHeight}px);
    `};
  }
  :first-child {
    margin-top: 20px;
    ${media.mobile`
      margin-top: 12px;
    `};
  }
`;

export default MessageCategoryTabAllInOne;
